import isBoolean from 'lodash-es/isBoolean';
import { TerminalType } from '../common/types';
import { ThemeType } from '../components/page-view/types';
import {
  flowMobileHiddenComs,
  flowPcHiddenComs,
  gridMobileHiddenComs,
  gridPcHiddenComs,
  LayoutType,
  PageModuleType,
} from '../constants';
import ebdcoms from './ebdcoms';
import { IPaySupportModulesData } from './page-style';

const EBPaySupportModulesCacheId = 'EBPaySupportModules';

const viewUtils = {
  /**
   * 获取页面入口请求的url
   * @param type 页面类型
   */
  getEntranceUrl(servicePath: string, type?: string) {
    if (type === PageModuleType.Portal || type === 'PORTAL') {
      return '/api/portal/mainpage/view';
    }
    if (type === PageModuleType.EbForm) {
      return '/api/ebuilder/form/viewdesigner/viewPageInfo';
    }
    if (type === PageModuleType.Synergy) {
      return '/api/portal/synergy/view';
    }
    if (type === PageModuleType.LoginPage) {
      return '/api/bs/basicserver/newloginskin/getEbPageLayout';
    }
    if (type === PageModuleType.ThemePage) {
      return '/api/basicserver/topic/getTopicEbPage';
    }
    // bi仪表盘获取数据
    if (type === PageModuleType.BI) {
      return `/api/${servicePath}/bi/dashboard/view`.replace(/\/+/g, '/');
    }
    // bi仪表盘右侧推出EB页面，走page的entrance 接口
    if (type === PageModuleType.Eb && servicePath === 'analyze/designer') {
      return '/api/ebuilder/page/view/entrance';
    }

    let ajaxUrl = servicePath
      ? `/api/${servicePath}/ebpage/view/entrance`.replace(/\/+/g, '/')
      : '/api/ebuilder/page/view/entrance';

    const isFreePass = window?.ePassport?.isFreePassUrl;

    // 针对免登录页面，将url上的api替换为papi
    if (isFreePass) {
      ajaxUrl = ajaxUrl.replace('/api/', '/papi/');
    }

    return ajaxUrl;
  },
  async formatPageData<
    T extends {
      comps: any[];
      config: any;
      compStyles?: any[];
      themeData?: ThemeType;
      layoutType: LayoutType;
    }
  >(page: T): Promise<T> {
    await ebdcoms.load();

    const { compatible } = ebdcoms.get();

    if (compatible) {
      page.comps = page.comps.map(compatible.formatCom);
      if (page.config) {
        page.config = compatible.formatPageStyle(page.config);
      }
    }
    if (
      (page.compStyles && page.compStyles.length > 0)
      || (page.themeData?.refStyles && page.themeData.refStyles.length > 0)
    ) {
      page.comps = this.formatComsLibStyle(page.comps, page.compStyles, page.themeData);
    }

    return page;
  },

  getPageParams(pageParam: any[]) {
    const params = {} as any;

    if (pageParam && pageParam.length > 0) {
      pageParam.forEach((p) => {
        const name = p.nameType === 'FORMFIELDS' ? p.name.id : p.name;
        if (p.type === 'fixed' || !p.type) {
          params[name] = p.value;
        } else if (p.type === 'field') {
          params[name] = p.value.id;
        }
      });
    }
    return params;
  },

  isHiddenCom(layoutType: LayoutType, clientType: TerminalType, comType: string) {
    if (!layoutType || !comType) return;

    if (layoutType === LayoutType.GridLayout && clientType === 'PC') {
      return gridPcHiddenComs.includes(comType);
    }
    if (layoutType === LayoutType.GridLayout && clientType === 'MOBILE') {
      return gridMobileHiddenComs.includes(comType);
    }
    if (layoutType === LayoutType.FlowLayout && clientType === 'PC') {
      return flowPcHiddenComs.includes(comType);
    }
    if (layoutType === LayoutType.FlowLayout && clientType === 'MOBILE') {
      return flowMobileHiddenComs.includes(comType);
    }
  },

  getPaySupportModules() {
    if (!window.EBPaySupportModules) {
      const cacheDataStr = localStorage.getItem(EBPaySupportModulesCacheId);
      if (cacheDataStr) {
        try {
          const cacheData = JSON.parse(cacheDataStr);
          const now = new Date().getTime();
          if (cacheData.expiredTime > now && cacheData.data) {
            return Promise.resolve(cacheData.data).then(() => {
              // 已经支持付费控制的模块列表
              window.EBPaySupportModules = cacheData.data;
            });
          }
        } catch (error) {
          console.error(error);
        }
      }
      return ebdcoms
        .asyncExcu('ajax')({
          url: '/api/bs/ebuilder/common/tenant/listTenantModuleType',
          success: (data: IPaySupportModulesData) => {
            // 已经支持付费控制的模块列表
            window.EBPaySupportModules = data;
          },
        })
        .then((data) => {
          try {
            // 一天的有效期
            const expiredTime = new Date().getTime() + 86400000;
            const cacheData = {
              expiredTime,
              data,
            };
            localStorage.setItem(EBPaySupportModulesCacheId, JSON.stringify(cacheData));
          } catch (error) {
            console.error(error);
          }
        })
        .catch(() => Promise.resolve(true));
    }
    return Promise.resolve(true);
  },

  formatComsLibStyle(comps: any[], _styles?: any[], themeData?: ThemeType, opts?: any) {
    return comps.map((com) => viewUtils.formatComLibStyle(com, _styles, themeData, opts));
  },

  formatComLibStyle(com: any, _styles?: any[], themeData?: ThemeType, opts?: any) {
    const styles = _styles || [];
    // 老页面部分组件特殊处理，容器未配置样式卡片，并且开启自定义按钮后，主题样式不覆盖组件样式
    // 新拖的这部分组件不走这部分逻辑，保持跟普通组件一致
    // 后端合并逻辑迁移至前端
    const coms = [
      'GridContainer',
      'Container',
      'MultiContent',
      'MultiColumn',
      'GridMultiColumn',
      'SimpleText',
      'EbButton',
      'Picture',
      'HelpTip',
      'FloatButton',
      'InternalContainer',
    ];
    const excludeCategory = ['15', '9', '16']; /** 排行榜、倒计时、时间轴三个组件一定会有组件样式 */
    const refStylesObj: any = {};
    const { isSetTheme, isPaste } = opts || {};
    const { refStyles: _refStyles, themeId = '' } = themeData || ({} as any);
    (_refStyles || []).forEach((style: any) => {
      refStylesObj[style.category] = {
        libStyle: style.config,
        themeLibStyle: style.config,
        id: style.id,
      };
    });

    const isExculdeCom = coms.includes(com.type);
    let applyRefStyles = com.applyStyle ? com.applyStyle.split(',') : [];
    if (isSetTheme && com.config && com.config.layout) {
      com.config.layout.selected = false;
    }
    if (com.config?.styles && com.config?.styles.length > 0) {
      com.config.styles.forEach((el: any) => {
        if (el.category === '7') {
          el.useComStyle = true;
          if (isExculdeCom && com.config.styles.length === 1) {
            // 特殊类型组件有种数据只有容器样式，没有基础样式，需要兼容这种数据格式，这种特殊组件数据也不与主题合并
            applyRefStyles = applyRefStyles.filter((category: string) => category !== '1');
          }
          return;
        }
        // 主题设置时，如果组件已经有自己的组件样式了，不需要再进行设置
        if (isSetTheme && el.useComStyle && el.id && el.libStyle) {
          applyRefStyles = applyRefStyles.filter((category: string) => category !== el.category);
          return;
        }
        const isSpecialCategory = excludeCategory.includes(el.category);
        const refStyle = refStylesObj[el.category];
        const isThemeStyle = !!(!el.useComStyle && el.id && el.themeId);
        const copyLibStyle = { ...el.libStyle };
        /** 在设计器切换组件样式的时候，styles里面会没有最新的组件样式，导致样式丢失 */
        const hasComStyleLib = el.useComStyle && el.libStyle;
        el.themeLibStyle = refStyle?.libStyle;
        el.themeLibStyleId = refStyle?.id || '';
        el.themeId = themeId || '';
        if (isSpecialCategory) {
          if (!isBoolean(el.useComStyle)) {
            el.useComStyle = true;
          }
        } else {
          el.useComStyle = false;
          el.libStyle = undefined;
        }
        if (el.id && (!isThemeStyle || (isSpecialCategory && el.useComStyle))) {
          const style = styles.find((s: any) => s.id === el.id);
          applyRefStyles = applyRefStyles.filter((category: string) => category !== el.category);
          if (isPaste) {
            // 粘贴时，如果组件自身设置了组件样式或外观卡片，则不应用主题样式
            el.libStyle = copyLibStyle;
            el.useComStyle = true;
          } else if (style || isSpecialCategory || hasComStyleLib) {
            // 组件自身设置了组件样式或外观卡片
            if (!el.libStyle) {
              el.libStyle = style?.config || copyLibStyle;
            }
            el.useComStyle = true; /** 解决设计器回显配置时，判断是不是组件自身选择的组件样式 */
          }
        } else if (!isExculdeCom || (isExculdeCom && (!el.useCustom || el.styleVersion === '1'))) {
          applyRefStyles = applyRefStyles.filter((category: string) => category !== el.category);
          // 组件合并主题关联样式
          el.libStyle = refStyle?.libStyle;
          el.id = refStyle?.id || '';
          if (isSetTheme && el.useCustom && refStyle?.libStyle) {
            // 组件样式卡片切换逻辑
            // 1. 组件没开自定义时，切换组件样式，不会清除组件之前的自定义样式
            // 2. 组件开了自定义样式时，切换组件样式，只会清除组件的配置样式，组件自定义的css源码不会清除
            // 主题切换保持相同逻辑处理，确保主题关联组件样式之后，执行同逻辑
            if (el.customStyle?.base && el.customStyle.base.codeSource) {
              el.customStyle = {
                base: { codeSource: (el.customStyle.base as any).codeSource },
                hasCustomStyle: true,
              } as any;
            } else {
              el.customStyle = undefined; // 切换选择时清除自定义样式
            }
          }
        } else if (isExculdeCom && el.useCustom) {
          // 特殊组件如果只开启了自定义，就不能追加主题样式
          applyRefStyles = applyRefStyles.filter((category: string) => category !== el.category);
        }
      });
    }
    if (Object.keys(refStylesObj).length > 0) {
      // 组件使用主题关联样式
      applyRefStyles.forEach((category: string) => {
        const style = refStylesObj[category];
        if (style) {
          const themStyle = {
            category,
            libStyle: style.libStyle,
            themeLibStyle: style.themeLibStyle,
            themeLibStyleId: style.id,
            id: style.id,
            themeId,
            useCustom: false,
            useComStyle: false,
          };
          if (category === '1' && com.config.styles) {
            const basicContainerStyle = com.config.styles?.find((el: any) => el.category === '7');
            themStyle.useCustom = basicContainerStyle?.useCustom || false;
            com.config.styles = [themStyle, ...com.config.styles];
          } else if (com.config?.styles) {
            com.config.styles = [...com.config.styles, themStyle];
          } else {
            com.config.styles = [themStyle];
          }
        }
      });
    }

    return com;
  },

  async formatPageStyle<
    T extends {
      comps: any[];
      config: any;
      compStyles?: any[];
      themeData?: ThemeType;
      layoutType: LayoutType;
    }
  >(page: T): Promise<T> {
    await ebdcoms.load();

    const { compatible } = ebdcoms.get();

    if (compatible && page.config) {
      page.config = compatible.formatPageStyle(page.config);
    }

    return page;
  },
};

export default viewUtils;
